import axios from 'axios';
import {
  convertBase64ToFile,
  fetchAndConvertToBase64,
  getAPIErrorReason,
  getFirebaseUrlFromFile,
  getHoursFromTime,
  isEmpty
} from '../helpers/common';
import { REACT_APP_TEAMCAMP_APIURL } from '../global/environment';
import {
  CommentFilesInterface,
  EmojiInterface,
  TaskGroupList,
  CreateTaskInputInterface,
  taskLinkInterface,
  TaskGroupInterface,
  GenerateSubTaskInputInterface,
  DataCommentsInterface,
  UpdateTaskPayload,
  TaskLinkList,
  TaskWithComments,
  TimeLogItem,
  FigmaPreviewUrlPayload,
  RecurringTaskGroup,
  UpdateBulkStatusPayload,
  FigmaPreviewResponseInterface
} from '../interfaces/TaskInterface';
import { TaskLink } from '../mobx/interfaces/task';
import { captureException } from './logService';
import { rootStore } from '../mobx/rootStore';
import { formatToRawComment, formatToRawTask } from '../mobx/common';
import { UserModel } from '../mobx/models/user';
import { TaskModel } from '../mobx/models/task';

/**
 * @desc Update task data
 * @param {*}
 */
export const updateTaskDetails = async (taskId: string | undefined, payload: UpdateTaskPayload) => {
  try {
    rootStore.taskStore.setLoading(true);
    if (!taskId) return false;
    const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/Tasks/updateTask/${taskId}`, payload);
    if (response) {
      if (payload.isArchived) {
        const task = rootStore.taskStore.tasks.find((t) => t.id === taskId);
        const subTaskCount = task?.subTasks?.length || 0;
        rootStore.workspaceStore.setWorkspaceTotalTask(
          Math.max(0, rootStore.workspaceStore.workspaceTotalTask - 1 - subTaskCount)
        );
      }
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to update task list please try again';
    captureException(error);
    dispatchTaskError(error);
    return false;
  } finally {
    rootStore.taskStore.setLoading(false);
  }
};

export const getRawTaskLinks = (item: TaskLinkList): TaskLink => {
  return {
    createdBy: item?.CreatedBy,
    createdTime: item?.CreatedTime,
    iconUrl: item?.IconUrl,
    label: item?.Label,
    readonly: item?.Readonly,
    reference: item?.Reference,
    type: item?.Type?.toString() || '',
    updatedBy: item?.UpdatedBy,
    updatedTime: item?.UpdatedTime,
    id: item?.['_id']
  };
};

/**
 * @desc Apply a task-details response to the task it was requested for, so a late response
 * doesn't overwrite whichever task is open after switching between a task and its subtask.
 * @param {*} data -The task details response.
 * @param {*} taskId -The task the response was requested for.
 */
export const updateCurrentTaskData = (data: TaskWithComments, taskId?: string | null) => {
  const targetTaskId = taskId || data?.id;
  const task = rootStore.taskStore.getTaskById(targetTaskId);
  if (!task) return;

  const commentData = data?.Comments?.map(formatToRawComment) || [];
  task.setComments(commentData);
  task.setFiles(data?.Files || []);
  // Skip when the field is missing so a partial response can't clear existing PRs.
  if (data?.PullRequests) {
    task.setPullRequests(data.PullRequests);
  }
};

/**
 * @desc Get task details data
 * @param {*} taskId -The unique identifier of the task to fetch details.
 */
export const getMyTaskDetailsData = async (taskId: string | undefined | null) => {
  try {
    rootStore.taskStore.setLoading(true);
    if (!taskId) return false;
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/Tasks/getTaskDetails/${taskId}`);
    const { data } = response;

    if (!data) return false;

    //Update Mobx Task Details
    updateCurrentTaskData(data, taskId);

    return data;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get my task details please try again';
    captureException(error);
    dispatchTaskError(error);
    return false;
  } finally {
    rootStore.taskStore.setLoading(false);
  }
};

/**
 * @desc create new comment
 * @param {*} taskId -- The unique identifier of the task to fetch details.
 */
export const createNewComment = async (taskId: string | undefined, payload?: DataCommentsInterface) => {
  try {
    rootStore.taskStore.setLoading(true);
    if (!taskId) return false;
    const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/Tasks/addComment/${taskId}`, payload);
    const { data } = response;
    if (data) {
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to create new comment please try again';
    captureException(error);
    dispatchTaskError(error);
    return false;
  } finally {
    rootStore.taskStore.setLoading(false);
  }
};

/**
 * @desc Update task comment
 * @param {*} taskId commentId payload
 */
export const updateTaskComment = async (
  taskId: string | undefined,
  commentId: string,
  payload: {
    Comment?: string;
    IsArchived?: boolean;
    Files?: CommentFilesInterface[];
  }
) => {
  try {
    rootStore.taskStore.setLoading(true);
    if (!taskId) return false;
    const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/Tasks/${taskId}/comment/${commentId}`, payload);
    const { data } = response;
    if (data) {
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to update comment please try again';
    captureException(error);
    dispatchTaskError(getAPIErrorReason(e) || 'Unable to update comment please try again');
    return false;
  } finally {
    rootStore.taskStore.setLoading(false);
  }
};

// In-flight getTimelogData requests, so a concurrent identical load can reuse one instead of scanning again.
const timelogRequests = new Map<string, ReturnType<typeof fetchTimelogData>>();

/**
 * @desc get timelog data
 * Parent tasks with sub tasks also get their sub tasks tracked total from the same request.
 * @param {boolean} reuseInFlight - Reuse an identical in-flight request instead of starting a new one.
 */
export const getTimelogData = (projectId: string | undefined, taskId: string | undefined, reuseInFlight = false) => {
  const task = taskId ? rootStore.taskStore.getTaskById(taskId) : undefined;
  const subTaskCount = task && !task.parentTaskId ? task.subTasks.length : 0;
  // Project and sub task count in the key so a request for another project, or started before sub tasks changed, isn't reused.
  const key = `${projectId}|${taskId}|${subTaskCount}`;
  const inFlight = timelogRequests.get(key);
  if (reuseInFlight && inFlight) return inFlight;
  const request = fetchTimelogData(projectId, taskId, subTaskCount > 0);
  if (taskId) {
    timelogRequests.set(key, request);
    request.finally(() => {
      if (timelogRequests.get(key) === request) timelogRequests.delete(key);
    });
  }
  return request;
};

const fetchTimelogData = async (
  projectId: string | undefined,
  taskId: string | undefined,
  includeSubTasks: boolean
) => {
  try {
    rootStore.taskStore.setLoading(true);
    if (!projectId || !taskId) return;

    const params = {
      projectId: projectId,
      taskId: taskId,
      ...(includeSubTasks && { includeSubTasks: true })
    };
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/timeLog/getTimeLogByTask`, {
      params
    });
    const { data } = response;
    if (data) {
      // Without includeSubTasks (or on an older backend) the response is the plain list.
      const timeLogs = Array.isArray(data) ? data : data.timeLogs;
      const updatedData = timeLogs?.map((item: TimeLogItem) => {
        const userName =
          rootStore.workspaceStore.currentWorkspace?.users?.find((user: UserModel) => user?.id === item?.createdBy)
            ?.name || 'Unknown';
        return {
          ...item,
          userName,
          hours: item?.timerStartedAt ? getHoursFromTime(new Date(item.timerStartedAt)) : item?.hours
        };
      });

      // Route to the requested task so a late response can't overwrite another task's logs.
      const requestedTask = rootStore.taskStore.getTaskById(taskId);
      requestedTask?.setTimeLogs(updatedData);
      if (!Array.isArray(data) && data.subTasks) requestedTask?.setSubTasksTimeLog(data.subTasks);
      return updatedData;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'unable to get timelog data for task please try again';
    captureException(error);
    dispatchTaskError(error);
    return false;
  } finally {
    rootStore.taskStore.setLoading(false);
  }
};

/**
 * @desc Project - get project task
 * @param {string} project_id - The unique identifier of the project to fetch task groups.
 * @param {boolean} isForceLoading - Flag to force loading of task groups.
 */
export const getTaskGroupList = async (project_id: string | undefined, isSelect?: boolean, isForceLoading = true) => {
  try {
    rootStore.taskStore.setLoading(true);
    if (!project_id) return;

    if (!isForceLoading) {
      const taskGroups = rootStore.projectStore.projects.find((project) => project.id === project_id)?.taskGroups;
      if (taskGroups && taskGroups?.length > 0) return taskGroups;
    }
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/taskGroup/getTaskGroupList/${project_id}`);
    const { data } = response;
    data.sort((a: { Index: number }, b: { Index: number }) => {
      return a.Index - b.Index;
    });
    if (data.length > 0) {
      const newData: TaskGroupInterface[] = data.map((element: TaskGroupList) => {
        return {
          id: element['_id'],
          index: element?.Index,
          name: element?.Name,
          projectId: element?.ProjectId,
          isCollapse: false
        };
      });

      //mobx set
      rootStore.projectStore.projects.find((project) => project.id === project_id)?.setTaskGroups(newData);
      return newData;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get task group list please try again';
    captureException(error);
    dispatchTaskError(error);
    return false;
  } finally {
    rootStore.taskStore.setLoading(false);
  }
};

/**
 * @desc Project - deleteTaskGroup
 * @param {string} taskGroupId - ID of the task group to delete
 */
export const deleteTaskGroup = async (taskGroupId: string) => {
  try {
    rootStore.taskStore.setLoading(true);

    const params = {
      id: taskGroupId
    };
    const response = await axios.delete(`${REACT_APP_TEAMCAMP_APIURL}/taskGroup/deleteTaskGroup`, { params });
    if (response) {
      dispatchTaskSuccess(response.data.message);
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to delete task group please try again';
    captureException(error);
    dispatchTaskError(error);
    return false;
  } finally {
    rootStore.taskStore.setLoading(false);
  }
};

/**
 * @desc Project - tasks - create new task
 * @param {*}
 */
export const createNewTask = async (payload: CreateTaskInputInterface) => {
  try {
    rootStore.taskStore.setLoading(true);

    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/Tasks/createTask`, payload);
    const { data } = response;
    if (data) {
      rootStore.workspaceStore.setWorkspaceTotalTask(rootStore.workspaceStore.workspaceTotalTask + 1);
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to create task please try again';
    captureException(error);
    dispatchTaskError(error);
    return false;
  } finally {
    rootStore.taskStore.setLoading(false);
  }
};

/**
 * @desc Project - tasks - create new task
 * @param {*}
 */
export const createBulkTask = async (payload: CreateTaskInputInterface[]) => {
  try {
    rootStore.taskStore.setLoading(true);

    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/Tasks/createBulkTasks`, payload);
    const { data } = response;
    if (data) {
      rootStore.workspaceStore.setWorkspaceTotalTask(rootStore.workspaceStore.workspaceTotalTask + payload.length);
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to create task please try again';
    captureException(error);
    dispatchTaskError(error);
    return false;
  } finally {
    rootStore.taskStore.setLoading(false);
  }
};

/**
 *  @desc Project - update group
 * @param {boolean} isIndexSetting - Optional flag to bypass validation
 */
export const updateGroupDetail = async (
  payload: { name?: string; itemId?: string; userId?: string; index?: number },
  isIndexSetting = false
) => {
  try {
    rootStore.taskStore.setLoading(true);

    if (payload?.name !== undefined && isEmpty(payload?.name) && !isIndexSetting) {
      dispatchTaskError('Please enter task group name');
      return;
    }
    const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/taskGroup/putTaskGroup`, payload);
    if (response) {
      if (!isIndexSetting) dispatchTaskSuccess(response.data.message);
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to update group please try again';
    captureException(error);
    dispatchTaskError(error);
    return false;
  } finally {
    rootStore.taskStore.setLoading(false);
  }
};

/**
 *  @desc Project - create new group
 * @param {*}
 */
export const createNewGroup = async (payload: { name: string }) => {
  try {
    if (isEmpty(payload?.name)) {
      dispatchTaskError('Please enter task group name');
      return;
    }

    rootStore.taskStore.setLoading(true);

    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/taskGroup/postTaskGroup`, payload);
    if (response) {
      dispatchTaskSuccess(response.data.message);
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to create group please try again';
    captureException(error);
    dispatchTaskError(error);
    return false;
  } finally {
    rootStore.taskStore.setLoading(false);
  }
};

/**
 *  @desc Get Figma preview URL
 * @param {*}
 */
export const getFigmaPreviewUrl = async (payload: FigmaPreviewUrlPayload) => {
  let reference = 0;
  try {
    rootStore.taskStore.setLoading(true);
    if (!payload) return false;
    if (!payload.fileId) return false;
    if (!payload.nodes) return false;
    const { currentWorkspace: workspace } = rootStore.workspaceStore;
    const updatedPayload = { ...payload, companyId: workspace?.id };
    const fetchResultAfterDelay = async () => {
      try {
        reference = reference + 1;
        const response = await axios.post<FigmaPreviewResponseInterface>(
          `${REACT_APP_TEAMCAMP_APIURL}/Tasks/getFigmaPreviewUrl`,
          updatedPayload
        );
        return response;
      } catch (e) {
        if (reference <= 5) return fetchResultAfterDelay();
        dispatchTaskError('Unable to get figma preview. Refresh page and try again');
        return undefined;
      }
    };

    const response: { data: FigmaPreviewResponseInterface } | undefined = await fetchResultAfterDelay();
    if (response?.data) {
      const base64Data = await fetchAndConvertToBase64(response?.data?.filePreviewUrl);
      const fileData = await convertBase64ToFile(base64Data);
      const fileUrl = await getFirebaseUrlFromFile({ file: fileData }, 'figma-preview/');
      return { ...(response?.data || {}), filePreviewUrl: fileUrl };
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get figma preview';
    captureException(error);
    dispatchTaskError(error);
    return undefined;
  } finally {
    rootStore.taskStore.setLoading(false);
  }
};

/**
 *  @desc task - comment - add reaction
 * @param {*}
 */
export const addReactionOnCommect = async (payload: EmojiInterface) => {
  try {
    rootStore.taskStore.setLoading(true);
    if (!payload?.taskId && !payload?.companyId) return false;

    const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/Tasks/emojiReactions`, payload);
    if (response) {
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to add reaction in comment please try again';
    captureException(error);
    dispatchTaskError(error);
    return false;
  } finally {
    rootStore.taskStore.setLoading(false);
  }
};

/**
 * @desc Get all recurring tasks
 * @param {*}
 */
export const getAllRecurringTasks = async () => {
  try {
    rootStore.taskStore.setLoading(true);

    const response = await axios.get<RecurringTaskGroup[]>(`${REACT_APP_TEAMCAMP_APIURL}/Tasks/getAllRecurringTasks`);
    const { data } = response;
    if (data) {
      const allTasks = data.flatMap((item: RecurringTaskGroup) => item.tasks);
      const recurringTasks = allTasks.map(formatToRawTask);
      rootStore.taskStore.setRecurringTasks(recurringTasks);
      return recurringTasks;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get recurring tasks please try again';
    captureException(error);
    dispatchTaskError(error);
    return false;
  } finally {
    rootStore.taskStore.setLoading(false);
  }
};

/**
 * Delete a recurring task by its ID and associated project ID.
 * @param {string} taskId - ID of the recurring task
 * @param {string} project_id - ID of the project
 */
export const deleteRecurringTask = async (taskId: string, project_id: string) => {
  try {
    rootStore.taskStore.setLoading(true);

    if (!taskId) return false;
    const response = await axios.delete(
      `${REACT_APP_TEAMCAMP_APIURL}/Tasks/removeRecurringTask/${taskId}?projectId=${project_id}`
    );

    if (response) {
      rootStore.workspaceStore.setWorkspaceTotalTask(Math.max(0, rootStore.workspaceStore.workspaceTotalTask - 1));
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to delete recurring task  please try again';
    captureException(error);
    dispatchTaskError(error);
    return false;
  } finally {
    rootStore.taskStore.setLoading(false);
  }
};

/**
 * @desc Update recurring task data
 * @param {string} recurring_task_id - ID of the recurring task
 */
export const updateRecurringTaskDetails = async (recurring_task_id: string | undefined, payload: UpdateTaskPayload) => {
  try {
    rootStore.taskStore.setLoading(true);

    if (!recurring_task_id) return false;
    const response = await axios.put(
      `${REACT_APP_TEAMCAMP_APIURL}/Tasks/updateRecurringTask/${recurring_task_id}`,
      payload
    );
    if (response) {
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to update recurring task please try again';
    captureException(error);
    dispatchTaskError(error);
    return false;
  } finally {
    rootStore.taskStore.setLoading(false);
  }
};

/**
 * @desc get total number of task
 * @param {*}
 */
export const getTotalTaskCount = async () => {
  try {
    rootStore.taskStore.setLoading(true);

    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/Tasks/getTotalTasks`);
    const { data } = response;
    if (data) {
      rootStore.workspaceStore.setWorkspaceTotalTask(data?.totalTasks);
      return data;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get total number of task';
    captureException(error);
    dispatchTaskError(error);
    return false;
  } finally {
    rootStore.taskStore.setLoading(false);
  }
};

/**
 * @desc taskDetails - add resource
 * @param {string} taskId - ID of the task
 */
export const addTaskResource = async (taskId: string, payload: taskLinkInterface) => {
  try {
    rootStore.taskStore.setLoading(true);

    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/Tasks/${taskId}/links`, payload);
    const { data } = response;
    if (data) {
      return data;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'unable to add link please try again';
    captureException(error);
    dispatchTaskError(error);
    return undefined;
  } finally {
    rootStore.taskStore.setLoading(false);
  }
};

/**
 * @desc task - update resource
 * @param {string} taskId - ID of the task
 * @param {string} linkId - ID of the resource link
 */
export const updateTaskResource = async (
  taskId: string,
  linkId: string,
  payload: { label?: string; reference?: string }
) => {
  try {
    rootStore.taskStore.setLoading(true);

    const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/Tasks/${taskId}/links/${linkId}`, payload);
    const { data } = response;
    if (data) {
      return data;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'unable to add link please try again';
    captureException(error);
    dispatchTaskError(error);
    return undefined;
  } finally {
    rootStore.taskStore.setLoading(false);
  }
};

/**
 * @desc taskDetails - add resource
 * @param {string} taskId - ID of the task
 * @param {string} linkId - ID of the link to be deleted
 */
export const deleteTaskLink = async (taskId: string | null | undefined, linkId: string | undefined) => {
  try {
    rootStore.taskStore.setLoading(true);
    if (!taskId || !linkId) return false;

    const response = await axios.delete(`${REACT_APP_TEAMCAMP_APIURL}/Tasks/${taskId}/links/${linkId}`);
    const { data } = response;
    if (data) {
      return data;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to delete link please try again';
    captureException(error);
    dispatchTaskError(error);
  } finally {
    rootStore.taskStore.setLoading(false);
  }
};

// Mobx - New services
// Max number of page requests fired at once, to avoid overwhelming the server/browser.
const GET_ALL_TASKS_CONCURRENCY = 3;

type GetAllTasksResponse = { hasNextPage: boolean; nextPage: string; tasks: TaskModel[]; totalCount: number };

export const getAllRawTasks = async (lastUpdatedDate?: Date) => {
  try {
    const lastUpdatedTime = lastUpdatedDate ? lastUpdatedDate.toISOString() : null;

    const buildUrl = (page: number) => {
      let url = `${REACT_APP_TEAMCAMP_APIURL}/v2/Tasks/getAllTasks?page=${page}`;
      if (lastUpdatedTime) {
        url = url + `&lastUpdatedTime=${lastUpdatedTime}`;
      }
      return url;
    };

    const fetchPage = async (page: number) => {
      const { data } = await axios.get(buildUrl(page));
      return data as GetAllTasksResponse;
    };

    // Fetch the first page to learn the page size and the total task count.
    const { hasNextPage, tasks: firstTasks, totalCount } = await fetchPage(0);

    const allTasks: TaskModel[] = [...firstTasks];

    if (!hasNextPage) {
      return allTasks.map(formatToRawTask);
    }

    const pageSize = firstTasks.length;
    // Prefer totalCount to fetch every remaining page in parallel. Only trust it when it
    // is a usable number; otherwise fall back to sequential hasNextPage-driven paging so
    // we never silently stop after page 0 if the count is missing/zero.
    const canUseTotalCount = typeof totalCount === 'number' && totalCount > pageSize && pageSize > 0;

    if (canUseTotalCount) {
      const totalPages = Math.ceil(totalCount / pageSize);

      // Pages 1..totalPages-1 still need fetching (page 0 is already loaded).
      const remainingPages: number[] = [];
      for (let page = 1; page < totalPages; page++) {
        remainingPages.push(page);
      }

      // Fetch in concurrency-limited batches so we don't fire every request at once.
      // Use allSettled so a transient failure on one page doesn't discard pages that
      // already succeeded; collect the failures and retry them once below.
      const failedPages: number[] = [];
      for (let i = 0; i < remainingPages.length; i += GET_ALL_TASKS_CONCURRENCY) {
        const batch = remainingPages.slice(i, i + GET_ALL_TASKS_CONCURRENCY);
        const results = await Promise.allSettled(batch.map((page) => fetchPage(page)));
        for (let j = 0; j < results.length; j++) {
          const result = results[j];
          if (result.status === 'fulfilled') {
            allTasks.push(...result.value.tasks);
          } else {
            failedPages.push(batch[j]);
          }
        }
      }

      // Retry any failed pages once. If they still fail, let the error propagate to the
      // catch below rather than returning a partial (silently incomplete) task list.
      // Fetch in concurrency-limited batches so we don't flood the server.
      if (failedPages.length > 0) {
        for (let i = 0; i < failedPages.length; i += GET_ALL_TASKS_CONCURRENCY) {
          const batch = failedPages.slice(i, i + GET_ALL_TASKS_CONCURRENCY);
          const retried = await Promise.all(batch.map((page) => fetchPage(page)));
          for (let j = 0; j < retried.length; j++) {
            allTasks.push(...retried[j].tasks);
          }
        }
      }
    } else {
      // Fallback: walk pages sequentially until the API says there are no more.
      captureException(
        new Error(
          `getAllRawTasks: totalCount is missing or invalid (${totalCount}). Falling back to sequential paging.`
        )
      );
      let currentPage = 1;
      let morePages: boolean = hasNextPage;
      const MAX_PAGES = 1000;
      while (morePages && currentPage < MAX_PAGES) {
        const data = await fetchPage(currentPage);
        allTasks.push(...data.tasks);
        morePages = data.hasNextPage;
        currentPage = currentPage + 1;
      }
      if (morePages && currentPage >= MAX_PAGES) {
        captureException(new Error(`getAllRawTasks: hit fallback page cap of ${MAX_PAGES} pages.`));
      }
    }

    return allTasks.map(formatToRawTask);
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get total number of task';
    captureException(error);
    // showError(error); remove comment
    return undefined;
  }
};

/**
 * @function getAllProjectRawTasks - Fetches all tasks for specific projects and formats them into RawTask format
 * @param {string[]} projectIds - Array of project IDs
 */
export const getAllProjectRawTasks = async (projectIds: string[]) => {
  try {
    const allTasks: TaskModel[] = [];
    let currentPage = 0;

    while (true) {
      let url = `${REACT_APP_TEAMCAMP_APIURL}/v2/Tasks/getAllProjectTasks?page=${currentPage}`;

      const { data } = await axios.post(url, { projectIds });
      const { hasNextPage, tasks } = data as { hasNextPage: boolean; nextPage: string; tasks: TaskModel[] };

      allTasks.push(...tasks);

      if (!hasNextPage) {
        break;
      }

      currentPage = currentPage + 1;
    }

    return allTasks.map(formatToRawTask);
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get total number of task';
    captureException(error);
    // showError(error); remove comment
    return [];
  }
};

export const updateBulkStatus = async (payload: UpdateBulkStatusPayload) => {
  try {
    rootStore.taskStore.setLoading(true);
    const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/Tasks/putTasksStatus`, payload);
    return response;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'unable to add link please try again';
    captureException(error);
    dispatchTaskError(error);
    return undefined;
  } finally {
    rootStore.taskStore.setLoading(false);
  }
};

/**
 * @desc Archive a collection of tasks in bulk.
 * @param payload - An object containing the projectId and an array of taskIds.
 * @param {string} payload.projectId - The ID of the project the tasks belong to.
 * @param {string[]} payload.taskIds - An array of task IDs to be archived.
 */
export const archiveBulkTasks = async (payload: { projectId: string; taskIds: string[] }) => {
  try {
    rootStore.taskStore.setLoading(true);
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/Tasks/archiveBulkTasks`, payload);

    if (response) {
      rootStore.workspaceStore.setWorkspaceTotalTask(
        Math.max(0, rootStore.workspaceStore.workspaceTotalTask - payload.taskIds.length)
      );
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to archive tasks, please try again.';
    captureException(error);
    dispatchTaskError(error);
    return false;
  } finally {
    rootStore.taskStore.setLoading(false);
  }
};

/**
 * @desc Project - tasks - generate sub task
 * @param {*}
 */
export const generateSubTasks = async (payload: GenerateSubTaskInputInterface) => {
  try {
    rootStore.taskStore.setLoading(true);
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/openai/generateSubTasks`, payload);
    const { data } = response;
    if (data) {
      return data;
    }
    return null;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to generate sub task please try again';
    captureException(error);
    dispatchTaskError(error);
    return false;
  } finally {
    rootStore.taskStore.setLoading(false);
  }
};

export function dispatchTaskError(msg: string) {
  rootStore.alertNotificationStore.setErrorMessage(msg);
}

export function dispatchTaskSuccess(msg: string) {
  rootStore.alertNotificationStore.setSuccessMessage(msg);
}
